import type { OrderQuantityDto, OrderQuantityInputDto, PricingVector } from "client/api-v2";

export interface OrderQuantityFormRow {
  vector: PricingVector;
  measuredValue: number | null;
  measuredAt: string | null;
  pricedValue: string;
  overrideReason: string;
}

export function toQuantityFormRows(quantities: OrderQuantityDto[]): OrderQuantityFormRow[] {
  return quantities.map((quantity) => ({
    vector: quantity.vector,
    measuredValue: quantity.measuredValue,
    measuredAt: quantity.measuredAt,
    pricedValue: String(quantity.pricedValue),
    overrideReason: quantity.overrideReason ?? "",
  }));
}

export function isQuantityRowOverridden(row: Pick<OrderQuantityFormRow, "measuredValue" | "pricedValue">): boolean {
  if (row.measuredValue === null) {
    return false;
  }
  const parsed = Number(row.pricedValue.trim());
  return Number.isFinite(parsed) && parsed !== row.measuredValue;
}

export function restoredPricedValue(row: Pick<OrderQuantityFormRow, "measuredValue">): string {
  return row.measuredValue === null ? "" : String(row.measuredValue);
}

export function validateQuantityValue(value: string): string | true {
  const trimmed = value.trim();
  if (trimmed === "") {
    return "A quantity is required";
  }
  if (!/^\d+$/.test(trimmed)) {
    return "Quantity must be a whole number, zero or more";
  }
  return true;
}

export const maxPricedQuantity = 1_000_000;

const quantityTooLargeMessage = `Quantity must be ${maxPricedQuantity.toLocaleString("en-US")} or less`;

export function validatePricedQuantity(value: string): string | true {
  const problem = validateQuantityValue(value);
  if (problem !== true) {
    return problem;
  }
  return Number(value.trim()) > maxPricedQuantity ? quantityTooLargeMessage : true;
}

export function validateOverrideReason(
  row: Pick<OrderQuantityFormRow, "measuredValue" | "pricedValue" | "overrideReason">
): string | true {
  if (!isQuantityRowOverridden(row)) {
    return true;
  }
  return (
    row.overrideReason.trim() !== "" || "A reason is required when the priced quantity differs from what was measured"
  );
}

export function toOrderQuantityInputDtos(rows: OrderQuantityFormRow[]): OrderQuantityInputDto[] {
  return rows.map((row) => ({
    vector: row.vector,
    pricedValue: Number(row.pricedValue.trim()),
    overrideReason: isQuantityRowOverridden(row) ? row.overrideReason.trim() : null,
  }));
}
